// hooks/useStorageUrl.js
/* eslint-disable max-lines */

/*  ────────────────────────────────────────────────────────────────
 *  Resolve a Cloud-Storage **object path** → browser-usable URL.
 *
 *  ▸ Signed-in users .............................. getDownloadURL()
 *  ▸ Public-preview (headless screenshot engine) .. build plain GCS URL
 *  ▸ Public assets (icons / banners / screenshots) . fallback to plain GCS
 *
 *  The hook keeps local state so callers re-render automatically
 *  when either the URL or an error state changes.
 *  ─────────────────────────────────────────────────────────────── */
import { useEffect, useState } from 'react';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage }            from '../firebase';

/* ────────────────────────────────────────────────────────────────
 * Enterprise bucket resolution
 * ----------------------------------------------------------------
 * WHY
 *  • Staging and production use different Firebase projects/buckets.
 *  • Hardcoding the prod bucket causes staging assets to resolve against
 *    the wrong Storage bucket.
 *  • The initialized Firebase Storage instance already knows the active
 *    environment bucket.
 * ──────────────────────────────────────────────────────────────── */
const getConfiguredStorageBucket = () => {
  const bucket =
    storage?.app?.options?.storageBucket ||
    process.env.REACT_APP_FIREBASE_STORAGE_BUCKET ||
    '';

  return String(bucket || '').trim();
};

/** Helper → craft an anonymous URL that anyone can GET. */
const buildPublicUrl = (objectPath) => {
  const bucket = getConfiguredStorageBucket();

  if (!bucket) {
    console.warn(
      '[useStorageUrl] Cannot build public URL because storageBucket is missing.'
    );
    return null;
  }

  const safe =
    String(objectPath || '')
      .split('/')
      .map((seg) => encodeURIComponent(seg))
      .join('/');

  return `https://storage.googleapis.com/${bucket}/${safe}`;
};

/* ────────────────────────────────────────────────────────────────
 * Preview-mode detector
 * - Supports BOTH legacy "true" and storageUtils wrapper { value:true, timestamp }
 * ──────────────────────────────────────────────────────────────── */
const readIsPublicPreview = () => {
  try {
    if (typeof window === 'undefined') return false;

    const raw = window.sessionStorage?.getItem('isPublicPreview');

    // legacy compatibility
    if (raw === 'true') return true;

    // storageUtils wrapper compatibility
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && parsed.value === true) return true;
      } catch (_) {
        // ignore parse failure; fall through
      }
    }

    return false;
  } catch (_) {
    return false;
  }
};

/* ────────────────────────────────────────────────────────────────
 * Public fallback policy (DAM-safe)
 *
 * Backend makes public ONLY for assetType in ["icon","banner","screenshot"].
 * Client callers that know the asset type should pass it into useStorageUrl().
 *
 * ENTERPRISE RULE
 *  • Public form-image assets may use stable anonymous GCS URLs.
 *  • Private DAM assets must not be guessed as public from path shape alone.
 *  • Public preview/headless screenshot can still use anonymous URLs because
 *    there is no authenticated Firebase user in that runtime.
 * ──────────────────────────────────────────────────────────────── */
const PUBLIC_FORM_IMAGE_TYPES = new Set(['icon', 'banner', 'screenshot']);

const normalizeAssetType = (assetType) => {
  const raw =
    assetType == null
      ? ''
      : String(assetType)
          .trim()
          .toLowerCase()
          .replace(/[\s_-]+/g, '');

  if (!raw) return '';

  if (raw === 'formpreview' || raw === 'formpreviewimage') {
    return 'screenshot';
  }

  if (raw === 'preview') {
    return 'screenshot';
  }

  return raw;
};

const isPublicFormImageAssetType = (assetType) => {
  const normalized = normalizeAssetType(assetType);
  return PUBLIC_FORM_IMAGE_TYPES.has(normalized);
};

const isEligiblePublicDerivativePath = (p) => {
  if (!p || typeof p !== 'string') return false;
  if (!p.startsWith('assets/')) return false;

  const looksLikePublicDerivative =
    // legacy + general derivatives
    /\/v\d+_thumbnail\.(webp|png|jpg|jpeg)$/i.test(p) ||
    /\/v\d+_grid_(400|800)\.webp$/i.test(p) ||
    /\/v\d+_micro_(32|64)\.webp$/i.test(p) ||

    // originals are allowed ONLY for explicit public form-image asset types.
    // If you later decide originals should remain private, remove this line.
    /\/v\d+_original\.(webp|png|jpg|jpeg)$/i.test(p) ||

    // icon/banner renditions that ImageFieldComponent actually requests
    /\/v\d+_icon2x\.(webp|png|jpg|jpeg)$/i.test(p) ||
    /\/v\d+_icon\.(webp|png|jpg|jpeg)$/i.test(p) ||
    /\/v\d+_banner2x\.(webp|png|jpg|jpeg)$/i.test(p) ||
    /\/v\d+_banner\.(webp|png|jpg|jpeg)$/i.test(p);

  return looksLikePublicDerivative;
};

const isEligibleForPublicFallback = (p, assetType = null) => {
  if (!isEligiblePublicDerivativePath(p)) return false;

  // Do not infer public access from path alone in authenticated app routes.
  // Private DAM assets can also have vN_grid_400.webp derivatives.
  return isPublicFormImageAssetType(assetType);
};

/* ────────────────────────────────────────────────────────────────
 * In-memory caches to reduce console/network spam in a single tab:
 * - unauthorizedCache: paths that returned storage/unauthorized
 * - inFlight: dedupe concurrent getDownloadURL() calls per path
 * ──────────────────────────────────────────────────────────────── */
const unauthorizedCache = new Set();  // Set<string>
const inFlight = new Map();           // Map<string, Promise<string>>

/**
 * React hook – turn a Storage *path* into a browser-usable URL.
 *
 * ENTERPRISE OPTIONS
 *  • assetType:
 *      Canonical system asset type when the caller knows it.
 *      Public-form image types are: icon, banner, screenshot.
 *
 *  • preferPublicUrl:
 *      When true and assetType is public-form-image, return the anonymous
 *      GCS URL immediately instead of trying Firebase Storage getDownloadURL().
 *
 *  • allowPublicFallback:
 *      Allows fallback to anonymous GCS URL after Firebase Storage denies
 *      getDownloadURL(). Defaults to true, but only applies when assetType is
 *      an explicit public-form-image type.
 *
 *  • debugLabel:
 *      Optional log context for future debugging.
 *
 * @param   {string | null} path
 *          e.g. "assets/…/v1_thumbnail.webp"
 * @param   {{
 *            assetType?: string|null,
 *            preferPublicUrl?: boolean,
 *            allowPublicFallback?: boolean,
 *            debugLabel?: string
 *          }} options
 * @returns {{ url: string | undefined, error: boolean, source: string | null }}
 *          `url`    – usable in <img>, undefined while loading / on failure
 *          `error`  – true when resolution failed
 *          `source` – diagnostic source: public-preview | public-direct |
 *                     firebase-download-url | public-fallback | null
 */
export const useStorageUrl = (path, options = {}) => {
  const [url, setUrl]       = useState();
  const [err, setErr]       = useState(false);
  const [source, setSource] = useState(null);

  const optionAssetType =
    options && typeof options === 'object'
      ? options.assetType ?? null
      : null;

  const preferPublicUrl =
    options && typeof options === 'object'
      ? options.preferPublicUrl === true
      : false;

  const allowPublicFallback =
    options && typeof options === 'object'
      ? options.allowPublicFallback !== false
      : true;

  const debugLabel =
    options && typeof options === 'object'
      ? options.debugLabel || ''
      : '';

  useEffect(() => {
    /*───────────────────────────────────────────────────────────────
     * Short-circuit when the caller gives us nothing.
     *──────────────────────────────────────────────────────────────*/
    if (!path) {
      setUrl(undefined);
      setErr(false);
      setSource(null);
      return;
    }

    let cancelled = false;

    /*───────────────────────────────────────────────────────────────
     * Enterprise stale-state guard
     * ----------------------------------------------------------------
     * WHY
     *  • Callers such as ImageFieldComponent can rotate candidate paths.
     *  • getDownloadURL() resolves asynchronously.
     *  • Without clearing the previous URL immediately, a caller can briefly
     *    render the prior candidate while the new candidate is resolving.
     *  • Without the `cancelled` guard below, an older async resolution can
     *    update state after this hook has already moved to a different path.
     *──────────────────────────────────────────────────────────────*/
    setUrl(undefined);
    setErr(false);
    setSource(null);

    /* Verbose log – helpful in DevTools Network/Console. */
//    console.log('[useStorageUrl] Resolving:', { path, optionAssetType, preferPublicUrl, debugLabel });

    /*──────────────── 1️⃣ Public-preview shortcut ────────────────*/
    const isPublicPreview = readIsPublicPreview();

    if (isPublicPreview) {
      const anonUrl = buildPublicUrl(path);
//      console.log('[useStorageUrl] Public-preview →', anonUrl);

      if (anonUrl) {
        setUrl(anonUrl);
        setErr(false);
        setSource('public-preview');
      } else {
        setUrl(undefined);
        setErr(true);
        setSource(null);
      }

      return;                        // ⬅ nothing else to do
    }

    /*──────────────── 1.5️⃣ Public-form-image direct URL ──────────
     * Enterprise rule:
     * - If caller explicitly tells us this path belongs to a public
     *   form-image asset type, do not call Firebase Storage SDK first.
     * - This avoids storage/unauthorized 403 noise in staging/prod.
     * - We never infer public access from path shape alone.
     *──────────────────────────────────────────────────────────────────────*/
    const canUsePublicFallback =
      allowPublicFallback &&
      isEligibleForPublicFallback(path, optionAssetType);

    if (preferPublicUrl && canUsePublicFallback) {
      const anonUrl = buildPublicUrl(path);

      if (anonUrl) {
        setUrl(anonUrl);
        setErr(false);
        setSource('public-direct');
        return;
      }
    }

    /*──────────────── 1.6️⃣ Known-unauthorized cache (kept for safety) ─────
     * If we already know Storage rules deny this path, avoid repeated
     * getDownloadURL() calls (prevents console/network spam).
     * - If it’s an explicit public-form-image derivative, fall back to anon URL.
     * - Otherwise, fail fast (private asset + unauthorized).
     *──────────────────────────────────────────────────────────────────────*/
    if (unauthorizedCache.has(path)) {
      if (canUsePublicFallback) {
        const anonUrl = buildPublicUrl(path);
//        console.log('[useStorageUrl] Cached unauthorized → public URL:', anonUrl);

        if (anonUrl) {
          setUrl(anonUrl);
          setErr(false);
          setSource('public-fallback');
        } else {
          setUrl(undefined);
          setErr(true);
          setSource(null);
        }

        return;
      }

      // Private + unauthorized: do NOT retry endlessly
      setUrl(undefined);
      setErr(true);
      setSource(null);
      return;
    }

    /*──────────────── 2️⃣ Private assets: Try signed URL (deduped) ─────────*/
    let p = inFlight.get(path);

    if (!p) {
      p = getDownloadURL(ref(storage, path));
      inFlight.set(path, p);
    }

    p.then((signedUrl) => {
        if (cancelled) return;

//        console.log('[useStorageUrl] Signed URL obtained ✅');
        setUrl(signedUrl);
        setErr(false);
        setSource('firebase-download-url');
      })
      .catch((firebaseErr) => {
        if (cancelled) return;

        /* storage/unauthorized (403) or storage/object-not-found (404) */
        console.warn('[useStorageUrl] getDownloadURL failed:', {
          code: firebaseErr?.code,
          path,
          assetType: optionAssetType,
          debugLabel,
        });

        const code = firebaseErr?.code || '';

        // Record unauthorized paths so we can avoid repeated attempts
        if (code === 'storage/unauthorized') {
          unauthorizedCache.add(path);
        }

        // Explicit public-form-image derivatives may fall back to anonymous
        // GCS URLs only after Storage resolution fails.
        if (canUsePublicFallback) {
          const anonUrl = buildPublicUrl(path);

          if (anonUrl) {
            setUrl(anonUrl);
            setErr(false);
            setSource('public-fallback');
            return;
          }
        }

        // Private assets, missing objects, or bucket config failures fail closed.
        setUrl(undefined);
        setErr(true);
        setSource(null);
      })
      .finally(() => {
        // Always clear in-flight once settled so future calls can re-check.
        // (Prevents “stuck promise” edge cases in HMR/dev.)
        if (inFlight.get(path) === p) {
          inFlight.delete(path);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    path,
    optionAssetType,
    preferPublicUrl,
    allowPublicFallback,
    debugLabel,
  ]);

  /* Expose BOTH pieces of state so callers can decide what to do. */
  return { url, error: err, source };
};
